import { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  Search,
  Loader2,
  CheckCircle2,
  AlertCircle,
  UploadCloud,
  ArrowUpCircle,
  UserRound,
} from "lucide-react";
import {
  useLazyAdminUpgradeMemberLookupQuery,
  useAdminRecordUpgradeBankPaymentMutation,
} from "../api/upgradeApi";
import { useGetLedgerAccountsQuery } from "../../billing/api/billingApi";
import { describeApiError } from "../../../lib/apiError";

type Member = {
  user_id: number;
  name: string;
  email?: string;
  membership_no?: string | null;
  advance_kobo?: number;
  current_grade?: string | null;
  target_grade?: string | null;
  upgradeable: boolean;
  not_upgradeable_reason?: string | null;
  requires_route: boolean;
  fee?: number | null;
  route_fees?: Record<string, number> | null;
  has_application: boolean;
  application_status?: string | null;
  application_complete?: boolean;
};

const naira = (n: number | null | undefined) =>
  n == null ? "—" : "₦" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function RecordBankPaymentModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}) {
  const [q, setQ] = useState("");
  const [member, setMember] = useState<Member | null>(null);

  const [route, setRoute] = useState("");
  const [amount, setAmount] = useState(""); // naira
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [bankAccountId, setBankAccountId] = useState("");
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const [msg, setMsg] = useState<string | null>(null);
  const [held, setHeld] = useState(false); // recorded but not submitted (requirements unmet)
  const [err, setErr] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<any[]>([]);

  const [triggerLookup, { data: lookup, isFetching: searching }] =
    useLazyAdminUpgradeMemberLookupQuery();
  const [record, { isLoading: saving }] = useAdminRecordUpgradeBankPaymentMutation();
  const { data: bankAccounts = [] } = useGetLedgerAccountsQuery();

  const results: Member[] = Array.isArray(lookup?.data) ? lookup!.data : [];

  // Reset everything each time the modal opens.
  useEffect(() => {
    if (open) {
      setQ("");
      setMember(null);
      setRoute("");
      setAmount("");
      setPaymentMethod("bank_transfer");
      setBankAccountId("");
      setReference("");
      setPaidAt("");
      setNote("");
      setFile(null);
      setMsg(null);
      setHeld(false);
      setErr(null);
      setBlockers([]);
    }
  }, [open]);

  // Debounced member search.
  const debounce = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (member) return; // stop searching once one is picked
    clearTimeout(debounce.current);
    if (q.trim().length < 2) return;
    debounce.current = setTimeout(() => triggerLookup(q.trim()), 300);
    return () => clearTimeout(debounce.current);
  }, [q, member, triggerLookup]);

  // The fee for the currently-selected member + route (naira).
  const computedFee = useMemo(() => {
    if (!member) return null;
    if (member.requires_route) return route ? member.route_fees?.[route] ?? null : null;
    return member.fee ?? null;
  }, [member, route]);

  // Pre-fill the amount with the computed fee whenever it changes (admin can edit).
  useEffect(() => {
    if (computedFee != null) setAmount(String(computedFee));
  }, [computedFee]);

  const pickMember = (m: Member) => {
    setErr(null);
    setMsg(null);
    setBlockers([]);
    setMember(m);
    setRoute("");
    setAmount(m.requires_route ? "" : m.fee != null ? String(m.fee) : "");
  };

  // Re-check eligibility from scratch whenever the route changes (a different
  // route has its own exam / CPD requirements).
  useEffect(() => {
    setBlockers([]);
  }, [route]);

  const submit = async (override = false) => {
    setErr(null);
    setMsg(null);
    if (!member) return setErr("Select a member first.");
    if (!member.upgradeable) return setErr(member.not_upgradeable_reason || "This member is not upgradeable.");
    if (member.requires_route && !route) return setErr("Select a route (exams or matured).");
    const amt = Number(amount);
    if (!amount || isNaN(amt) || amt <= 0) return setErr("Enter a valid amount.");

    const fd = new FormData();
    fd.append("user_id", String(member.user_id));
    if (route) fd.append("route", route);
    fd.append("amount", String(amt));
    fd.append("payment_method", paymentMethod);
    if (bankAccountId) fd.append("bank_account_id", bankAccountId);
    if (reference.trim()) fd.append("reference", reference.trim());
    if (paidAt) fd.append("paid_at", paidAt);
    if (note.trim()) fd.append("note", note.trim());
    if (file) fd.append("proof", file);
    if (override) fd.append("override", "1");

    try {
      const res: any = await record(fd).unwrap();
      setBlockers([]);
      setHeld(!!res?.held_ineligible);
      setMsg(res?.message || "Bank payment recorded.");
      onDone?.();
    } catch (e: any) {
      // The member fails the route's eligibility checks (exam result, CPD,
      // seniority). Surface them so the admin can consciously override.
      if (e?.data?.code === "UPGRADE_ELIGIBILITY_BLOCKED" && Array.isArray(e.data.blockers)) {
        setBlockers(e.data.blockers);
        setErr(null);
      } else {
        setBlockers([]);
        setErr(describeApiError(e, "Could not record the payment."));
      }
    }
  };

  if (!open) return null;

  const overpay = member && computedFee != null && Number(amount) > computedFee
    ? Number(amount) - computedFee
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 md:p-8">
      <div className="w-full max-w-lg bg-white rounded-[28px] shadow-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center justify-center">
              <ArrowUpCircle className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900 tracking-tight">Record Bank Payment</h2>
              <p className="text-xs font-semibold text-gray-500">Upgrade fee paid offline / to the bank</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Success state */}
          {msg ? (
            <div className="text-center py-6 space-y-4">
              {held ? (
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
              ) : (
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              )}
              <p className="text-sm font-semibold text-gray-700">{msg}</p>
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Step 1 — member search / selection */}
              {!member ? (
                <div className="space-y-3">
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-500">
                    Search member
                  </label>
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      autoFocus
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Name, email or membership no."
                      className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    />
                    {searching && (
                      <Loader2 className="w-4 h-4 text-gray-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />
                    )}
                  </div>

                  {q.trim().length >= 2 && (
                    <div className="border border-gray-100 rounded-xl divide-y divide-gray-50 max-h-64 overflow-y-auto">
                      {results.length === 0 && !searching ? (
                        <p className="text-sm text-gray-400 p-4 text-center">No members found.</p>
                      ) : (
                        results.map((m) => (
                          <button
                            key={m.user_id}
                            onClick={() => pickMember(m)}
                            className="w-full text-left px-4 py-3 hover:bg-indigo-50/50 flex items-center gap-3"
                          >
                            <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                              <UserRound className="w-4 h-4 text-gray-500" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-gray-900 truncate">{m.name}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {[m.membership_no, m.email].filter(Boolean).join(" · ")}
                              </p>
                              <p className="text-[11px] font-semibold text-indigo-600 mt-0.5">
                                {m.upgradeable
                                  ? `${m.current_grade ?? "—"} → ${m.target_grade}`
                                  : "Not upgradeable"}
                              </p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Selected member card */}
                  <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-black text-gray-900 truncate">{member.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {[member.membership_no, member.email].filter(Boolean).join(" · ")}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-bold border border-indigo-100">
                          {member.current_grade ?? "—"} → {member.target_grade ?? "—"}
                        </span>
                        {member.has_application ? (
                          <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold border border-emerald-100">
                            {member.application_complete ? "Application complete" : "Application in progress"}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-bold border border-amber-100">
                            No application yet
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setMember(null)}
                      className="text-xs font-bold text-gray-400 hover:text-gray-700 shrink-0"
                    >
                      Change
                    </button>
                  </div>

                  {!member.upgradeable && (
                    <p className="text-xs font-semibold text-red-500">
                      {member.not_upgradeable_reason || "This member is not upgradeable."}
                    </p>
                  )}

                  {/* Route (only when required) */}
                  {member.upgradeable && member.requires_route && (
                    <div>
                      <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                        Route
                      </label>
                      <select
                        value={route}
                        onChange={(e) => setRoute(e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                      >
                        <option value="">— Select route —</option>
                        {Object.keys(member.route_fees ?? {}).map((r) => (
                          <option key={r} value={r}>
                            {r.charAt(0).toUpperCase() + r.slice(1)} — {naira(member.route_fees?.[r])}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {member.upgradeable && (
                    <>
                      {/* Amount */}
                      <div>
                        <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                          Amount (₦)
                        </label>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                        />
                        <p className="text-[11px] text-gray-400 mt-1">
                          Fee: {naira(computedFee)}
                          {overpay > 0 && (
                            <span className="text-amber-600 font-semibold">
                              {" "}· Overpayment {naira(overpay)} → member advance
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Method + bank account */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                            Method
                          </label>
                          <select
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                          >
                            {/* Money that arrived outside the app is a bank
                                transfer — the bank account below records where
                                it landed — or cash. "Cheque" and "Other
                                (offline)" were here but are not stored methods:
                                both truncated on insert and failed the whole
                                recording. A cheque paid into an account is that
                                account's transfer. */}
                            <option value="bank_transfer">Bank transfer</option>
                            <option value="cash">Cash</option>
                            <option value="pos">POS</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                            Bank account
                          </label>
                          <select
                            value={bankAccountId}
                            onChange={(e) => setBankAccountId(e.target.value)}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                          >
                            <option value="">— Select —</option>
                            {(bankAccounts as any[]).map((acc: any) => (
                              <option key={acc.id} value={acc.id}>
                                {[acc.bank_name, acc.account_name ?? acc.name, acc.account_number]
                                  .filter(Boolean)
                                  .join(" — ")}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Reference + date */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                            Bank reference
                          </label>
                          <input
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="Optional"
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                            Payment date
                          </label>
                          <input
                            type="date"
                            value={paidAt}
                            onChange={(e) => setPaidAt(e.target.value)}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                          />
                        </div>
                      </div>

                      {/* Proof upload */}
                      <div>
                        <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                          Proof of payment
                        </label>
                        <label className="flex items-center gap-3 border border-dashed border-gray-300 rounded-xl px-4 py-3 cursor-pointer hover:bg-gray-50">
                          <UploadCloud className="w-5 h-5 text-gray-400" />
                          <span className="text-sm text-gray-600 truncate">
                            {file ? file.name : "Upload receipt (jpg, png, pdf)"}
                          </span>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                          />
                        </label>
                      </div>

                      {/* Note */}
                      <div>
                        <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                          Note
                        </label>
                        <textarea
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          rows={2}
                          placeholder="Optional — anything to record about this payment"
                          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {blockers.length > 0 && (
                <div className="text-sm bg-amber-50 border border-amber-200 rounded-xl px-3 py-3 space-y-2">
                  <div className="flex items-center gap-2 text-amber-800 font-bold">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>Member does not meet the requirements for this route</span>
                  </div>
                  <ul className="list-disc pl-6 space-y-1 text-amber-800/90">
                    {blockers.map((b: any, i: number) => (
                      <li key={b?.code ?? i}>
                        <span className="font-semibold">{b?.title ?? "Requirement not met"}</span>
                        {b?.message ? ` — ${b.message}` : ""}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] font-semibold text-amber-700/80">
                    The payment was made, so you can override and record it anyway — this will be logged against your account.
                  </p>
                </div>
              )}

              {err && (
                <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!msg && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white"
            >
              Cancel
            </button>
            <button
              onClick={() => submit(blockers.length > 0)}
              disabled={saving || !member || !member.upgradeable}
              className={`px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 flex items-center gap-2 ${
                blockers.length > 0 ? "bg-amber-600 hover:bg-amber-700" : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {blockers.length > 0 ? "Override & record anyway" : "Record payment"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
